/**
 * V37.43 — Helper UI pour scroller vers le premier champ en erreur lorsque
 * la validation d'un formulaire echoue silencieusement.
 *
 * Probleme commun resolu :
 *   Un utilisateur clique sur un bouton de soumission (« Calculer »,
 *   « Generer devis », « Sousscrire »...). Le formulaire affiche des
 *   erreurs INLINE en haut, mais le bouton est en bas. L'utilisateur,
 *   scrolle en bas, n'a aucun feedback → impression de « bouton mort ».
 *
 * Usage :
 *   import { showFormErrorFeedback } from 'lib/formFeedback';
 *
 *   if (hasValidationErrors) {
 *     showFormErrorFeedback({
 *       firstErrorTestId: 'ca-ht-input',  // data-testid du champ
 *       message: 'Veuillez corriger les erreurs avant de continuer',
 *       description: 'Les champs en rouge sont obligatoires.',
 *     });
 *     return;
 *   }
 */
import { toast } from 'sonner';

/**
 * Affiche un toast d'erreur ET scrolle vers le premier champ en erreur.
 *
 * @param {Object} opts
 * @param {string} [opts.firstErrorTestId]  data-testid du premier champ en erreur
 * @param {string} [opts.message]  Message principal du toast (defaut FR generique)
 * @param {string} [opts.description]  Sous-titre du toast (defaut FR generique)
 * @param {number} [opts.haloDurationMs=2200]  Duree du halo rouge (ms)
 * @param {boolean} [opts.focusInput=true]  Donne le focus a l'input/textarea
 * @param {string} [opts.fallbackSelector='.border-red-500, .ring-red-200']  Fallback CSS
 */
export function showFormErrorFeedback({
  firstErrorTestId,
  message = 'Veuillez corriger les erreurs du formulaire avant de continuer',
  description = "Les champs en rouge ont besoin d'être renseignés correctement.",
  haloDurationMs = 2200,
  focusInput = true,
  fallbackSelector = '.border-red-500, .ring-red-200',
} = {}) {
  // 1. Toast top-level (peu importe la position scroll)
  try {
    toast.error(message, { description, duration: 4500 });
  } catch { /* sonner indisponible, on continue le scroll */ }

  // 2. Scroll vers le champ apres le repaint pour que les classes red soient appliquees
  setTimeout(() => {
    let target = null;
    if (firstErrorTestId) {
      target = document.querySelector(`[data-testid="${firstErrorTestId}"]`);
    }
    if (!target) target = document.querySelector(fallbackSelector);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Halo rouge signaletique
    target.classList.add('ring-4', 'ring-red-400', 'ring-opacity-70');
    setTimeout(() => {
      target.classList.remove('ring-4', 'ring-red-400', 'ring-opacity-70');
    }, haloDurationMs);

    // Focus pour saisie immediate
    if (focusInput && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      try { target.focus({ preventScroll: true }); } catch { /* noop */ }
    }
  }, 80);
}
